import React, { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { isEmailFormatValid } from './authHelpers';
import { useAuthFlow } from './useAuthFlow';

import LoginForm from './LoginForm';
import NewPasswordForm from './NewPasswordForm';
import MFAForm from './MFAForm';
import TOTPSetupForm from './TOTPSetupForm';
import PasswordResetForm from './PasswordResetForm';

import styles from './Login.module.css';

interface LoginProps {
  appName: string;
  onAuthSuccess: (redirectTo: string) => void;
}

const LoginController: React.FC<LoginProps> = ({ appName, onAuthSuccess }) => {
  const [email, setEmail] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get('expired') === 'true';

  const location = useLocation(); // Get the location object from react-router-dom
  // Check if there's an intended destination stored by ProtectedRoute
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';

  const handleAuthComplete = async (): Promise<void> => {
    onAuthSuccess(from);
  };

  // Existing auth context and navigation
  const { state, actions } = useAuthFlow(appName, handleAuthComplete);

  useEffect(() => {
    setErrorMessage(state.errorMessage);
  }, [state.errorMessage]);

  const handleCancel = (): void => {
    actions.resetState();
    setEmail('');
  };

  const handleForgotPassword = (userEmail: string): void => {
    if (!isEmailFormatValid(userEmail)) {
      setErrorMessage('Please enter your email address to reset your password.');
      return;
    }
    setEmail(userEmail);
    setErrorMessage('');
    actions.requestPasswordReset(userEmail);
  };

  const handleNewPassword = (newPassword: string): void => {
    actions.confirmNewPassword(newPassword);
  };

  const handleMfaCode = (code: string): void => {
    actions.confirmMfaCode(code);
  };

  const handlePasswordReset = (confirmationCode: string, newPassword: string): void => {
    actions.confirmPasswordReset(email, confirmationCode, newPassword);
  };

  const handleSignIn = (userEmail: string, password: string): void => {
    setEmail(userEmail);
    actions.signInUser(userEmail, password);
  };

  const handleTotpSetup = (code: string): void => {
    actions.verifyTotpSetup(code);
  };

  const handleBackToLogin = (): void => {
    actions.resetState();
  };

  // This function determines which form component to render
  // (replaces all the complex conditional rendering in the original)
  const renderCurrentFlow = (): React.JSX.Element=> {
    switch (state.currentFlow) {
    case 'newPassword':
      return (
        <NewPasswordForm
          onSubmit={handleNewPassword}
          loading={state.loading}
          errorMessage={errorMessage}
        />
      );

    case 'mfa':
      return (
        <MFAForm
          mfaType={state.mfaType || 'TOTP'}
          onSubmit={handleMfaCode}
          loading={state.loading}
          errorMessage={errorMessage}
          message={state.message}
        />
      );

    case 'mfaSetup':
      return (
        <TOTPSetupForm
          email={email}
          appName={appName}
          totpSecret={state.totpSecret}
          totpUri={state.totpUri}
          onSubmit={handleTotpSetup}
          onCancel={handleCancel}
          loading={state.loading}
          errorMessage={errorMessage}
          message={state.message}
        />
      );

    case 'passwordReset':
      return (
        <PasswordResetForm
          email={email}
          onSubmit={handlePasswordReset}
          onBack={handleBackToLogin}
          loading={state.loading}
          errorMessage={errorMessage}
          message={state.message}
        />
      );

    default: // 'login' flow
      return (
        <LoginForm
          onSubmit={handleSignIn}
          onForgotPassword={handleForgotPassword}
          loading={state.loading}
          errorMessage={errorMessage}
          message={state.message}
        />
      );
    }
  };

  return (
    <div className={styles['login-screen']}>
      {/* Session Expired Message */}
      {sessionExpired && state.currentFlow === 'login' && (
        <div className={styles['session-expired-message']}>
          Your session has expired. Please log in again.
        </div>
      )}
      {renderCurrentFlow()}

      {/* Loading Overlay */}
      {state.loading && (
        <div className={styles['loading-overlay']}>
          <div className={styles['loading-spinner-container']}>
            <div className={styles['loading-spinner']}></div>
            <p className={styles['loading-text']}>Please wait...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginController;
